import { useStep } from "react-hooks-helper";
import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Toast } from "primereact/toast";
import { FooterButton, StyledAddShiftModal, StyledButtonContainer } from "./styledComponents";
import { CreateShiftScreen } from "./Screens/CreateShiftScreen";
import { SelectWorkersScreen } from "./Screens/SelectWorkersScreen";
import { AddShiftFormTypes, ValidatedAddShiftFormTypes, Position, AddShiftPartialResult } from "./AddShiftForm.types";
import { useAddShiftTimekeepingMutation, useGetAllWorkersQuery } from "../../../../api/bluecrew/hooks/payroll";
import { useGetCompanyV1Query } from "../../../../api/bluecrew/hooks/company";
import { CompanyWorker } from "../../../../api/bluecrew/payroll";
import { ConfirmWorkersScreen } from "./Screens/ConfirmWorkersScreen";
import { ErrorText } from "../../styledComponents";
import { getCreateNextShiftErrorMessage, getSelectWorkersErrorMessage } from "./addShiftUtils";
import { useAppSelector } from "../../../../redux";
import { getIsSplitioReady } from "../../../../redux/selectors/splitio";
import { FailedWorkersDisplay } from "./Components/FailedWorkersDisplay";
import { SuccessFailureModal } from "../../../../components/SuccessFailureModal/SuccessFailureModal";

enum AddShiftStep {
  CREATE_SHIFT,
  SELECT_WORKERS,
  CONFIRM_WORKERS,
  PARTIAL_SUCCESS,
}

export type AddShiftModalProps = {
  isModalOpen: boolean;
  setModalOpen: (modalOpen: boolean) => void;
};

const INITIAL_FORM_STATE = { breaks: [], breakDurationMins: 0 };

export const AddShiftModal = ({ isModalOpen, setModalOpen }: AddShiftModalProps) => {
  const companyId = useParams().internalCompanyId!!;
  const [selectedWorkers, setSelectedWorkers] = useState<Map<string, CompanyWorker>>(new Map());
  const [formState, setFormState] = useState<AddShiftFormTypes>(INITIAL_FORM_STATE);
  const [currentPosition, setCurrentPosition] = useState<Position>();
  const [error, setError] = useState<string>();
  const [partialSuccessResults, setPartialSuccessResults] = useState<Array<AddShiftPartialResult>>([]);
  const [apiErrorMessage, setApiErrorMessage] = useState<string>("");
  const [isErrorVisible, setIsErrorVisible] = useState(false);

  const numSteps = 4;
  const {
    index: step,
    navigation: { go },
  } = useStep({
    steps: numSteps,
    initialStep: AddShiftStep.CREATE_SHIFT,
  });

  // Using same query as <Timesheets> to take advantage of cache
  const isSplitIoReady = useAppSelector(getIsSplitioReady);
  const { data: companyDetails, isLoading: isCompanyFetching, isError: hasCompanyFailed }
    = useGetCompanyV1Query(parseInt(companyId, 10), isSplitIoReady);

  const { data: workers, isLoading: areWorkersFetching } = useGetAllWorkersQuery(companyId);

  const resetModal = () => {
    setModalOpen(false);
    setFormState(INITIAL_FORM_STATE);
    setSelectedWorkers(new Map());
    setPartialSuccessResults([]);
    setError(undefined);
    setApiErrorMessage("");
    setIsErrorVisible(false);
    go(AddShiftStep.CREATE_SHIFT);
  };

  const { mutate: sendAddShiftRequest, isLoading: isAddShiftInProgress } = useAddShiftTimekeepingMutation(
    () => {
      showSuccessToast();
      resetModal();
    },
    (message) => {
      setApiErrorMessage(message);
      setIsErrorVisible(true);
    },
    (results) => {
      handlePartialSuccess(results);
    }
  );

  const convertTZ = (timezoneString: string, date: Date) => {
    const newDate = new Date(
      date.toLocaleString("en-US", {
        timeZone: timezoneString,
      }),
    );
    const newDateMilli = newDate.valueOf();
    const dateMilli = date.valueOf();
    const difference = dateMilli - newDateMilli;
    return new Date(dateMilli + difference);
  };

  function formatTimestampsForPositionTimezone(
    currentFormState: ValidatedAddShiftFormTypes,
    position: Position,
  ) {
    const { timezone } = position;
    const { clockIn, clockOut, breaks } = currentFormState;
    const zonedClockIn = convertTZ(timezone, clockIn);
    const zonedClockOut = convertTZ(timezone, clockOut);
    const zonedBreaks = breaks.map(breakItem => ({
      start: convertTZ(timezone, breakItem.start),
      end: convertTZ(timezone, breakItem.end),
    }));
    return {
      ...currentFormState,
      clockIn: zonedClockIn,
      clockOut: zonedClockOut,
      breaks: zonedBreaks,
    };
  }

  const getHeaderTextFromStep = () => {
    switch (step) {
      case AddShiftStep.CREATE_SHIFT:
        return "Create a new shift";
      case AddShiftStep.SELECT_WORKERS:
        return "Select workers";
      case AddShiftStep.CONFIRM_WORKERS:
        return `Confirm adding ${selectedWorkers.size} Crew Member(s)`;
      case AddShiftStep.PARTIAL_SUCCESS:
        return "Partial Success";
      default:
        throw Error("Invalid Step");
    }
  };

  const getFooterTextFromStep = () => {
    switch (step) {
      case AddShiftStep.CREATE_SHIFT:
        return "Select workers";
      case AddShiftStep.SELECT_WORKERS:
        return "Confirm shift";
      case AddShiftStep.CONFIRM_WORKERS:
        return isAddShiftInProgress ? "Adding..." : "Add to shift";
      case AddShiftStep.PARTIAL_SUCCESS:
        return "Close";
      default:
        throw Error("Invalid Step");
    }
  };

  const toast = useRef<Toast>(null);
  const showSuccessToast = () => {
    toast.current?.show({
      severity: "success",
      summary: "Success",
      detail: "Added Crew Member(s) to shift",
    });
  };

  const handlePartialSuccess = (results: Array<AddShiftPartialResult>) => {
    setPartialSuccessResults(results);
    go(AddShiftStep.PARTIAL_SUCCESS);

    const successCount = results.filter(result => result.status === 'success').length;
    const failureCount = results.filter(result => result.status === 'error').length;
    toast.current?.show({
      severity: "warn",
      summary: "Partial Success",
      detail: `Successfully added ${successCount} crew member${successCount > 1 ? 's' : ''}` +
        ` to job, ${failureCount} crew member${failureCount > 1 ? 's' : ''} failed`,
    });
  };

  const nextHandler = () => {
    if (step === AddShiftStep.CREATE_SHIFT) {
      createShiftNextHandler();
    } else if (step === AddShiftStep.SELECT_WORKERS) {
      confirmWorkersNextHandler();
    } else if (step === AddShiftStep.CONFIRM_WORKERS) {
      if (validateFormState(formState) && !!currentPosition && !!companyDetails) {
        const zonedFormState = formatTimestampsForPositionTimezone(formState, currentPosition);
        const reqWorkers = Array.from(selectedWorkers.values());
        sendAddShiftRequest({
          companyId: companyDetails.id,
          position: zonedFormState.position,
          supervisor: zonedFormState.supervisor,
          clockIn: zonedFormState.clockIn,
          clockOut: zonedFormState.clockOut,
          breaks: zonedFormState.breaks,
          wageOverride: zonedFormState.wageOverride,
          workers: [...reqWorkers],
        });
      } else {
        setError('Unexpected error: some of the request values are internally undefined');
      }
    } else if (step === AddShiftStep.PARTIAL_SUCCESS) {
      resetModal();
    }
  };

  const createShiftNextHandler = () => {
    const stepErrors = getCreateNextShiftErrorMessage(formState);
    if (!stepErrors) {
      go(AddShiftStep.SELECT_WORKERS);
    }
    setError(stepErrors);
  };

  const confirmWorkersNextHandler = () => {
    const stepErrors = getSelectWorkersErrorMessage(selectedWorkers);
    if (!stepErrors) {
      go(AddShiftStep.CONFIRM_WORKERS);
    }
    setError(stepErrors);
  };

  const validateFormState = (object: AddShiftFormTypes) : object is ValidatedAddShiftFormTypes => {
    return !!object.position && !!object.supervisor && !!object.clockIn && !!object.clockOut
      && !!object.breaks && object.breaks.every(item => !!item.start && !!item.end) && Number.isFinite(object.breakDurationMins);
  }

  return (
    <>
      <Toast ref={toast} />
      <StyledAddShiftModal
        data-testid={"add-shift-modal"}
        header={getHeaderTextFromStep()}
        visible={isModalOpen}
        closable={!isAddShiftInProgress}
        onHide={() => resetModal()}
        footer={
          <StyledButtonContainer>
            <FooterButton
              disabled={areWorkersFetching || isCompanyFetching || isAddShiftInProgress || hasCompanyFailed}
              onClick={nextHandler}
            >
              {getFooterTextFromStep()}
            </FooterButton>
          </StyledButtonContainer>
        }
      >
        <>
          {step === AddShiftStep.CREATE_SHIFT && (
            <CreateShiftScreen
              formValues={formState}
              setFormValues={setFormState}
              setCurrentPosition={setCurrentPosition}
            />
          )}
          {step === AddShiftStep.SELECT_WORKERS && (
            <SelectWorkersScreen
              allWorkers={workers || []}
              selectedWorkers={selectedWorkers}
              setSelectedWorkers={setSelectedWorkers}
            />
          )}
          {step === AddShiftStep.CONFIRM_WORKERS && (
            <ConfirmWorkersScreen selectedWorkers={selectedWorkers} />
          )}
          {step === AddShiftStep.PARTIAL_SUCCESS && (
            <FailedWorkersDisplay
              workerProps={selectedWorkers}
              workerResults={partialSuccessResults}
            />
          )}
          {error && <ErrorText>{error}</ErrorText>}
          {hasCompanyFailed && <ErrorText>Error loading company data</ErrorText>}
        </>
      </StyledAddShiftModal>
      <SuccessFailureModal
        title="Unable to Add Shift"
        visible={isErrorVisible}
        onButtonClick={() => setIsErrorVisible(false)}
        isSuccess={false}
      >
        {apiErrorMessage}
      </SuccessFailureModal>
    </>
  );
};
